import { useState, useEffect } from 'react';
import {
  Mail,
  Search,
  Filter,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronRight,
  MessageSquare,
  User,
  RefreshCw,
  Send,
  Plus,
  X,
} from 'lucide-react';
import './Support.css';

interface EmailThread {
  id: string;
  externalId: string;
  discordThreadId?: string;
  fromAddress: string;
  fromName?: string;
  subject: string;
  status: 'open' | 'pending' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  createdAt: string;
  updatedAt: string;
  messageCount?: number;
  lastMessageAt?: string;
}

interface EmailMessage {
  id: string;
  threadId: string;
  direction: 'inbound' | 'outbound';
  bodyText?: string;
  bodyHtml?: string;
  sentAt: string;
}

interface ThreadWithMessages extends EmailThread {
  messages: EmailMessage[];
}

interface EmailStats {
  total: number;
  open: number;
  pending: number;
  closed: number;
  urgent: number;
  high: number;
  newToday: number;
}

const defaultStats: EmailStats = {
  total: 0,
  open: 0,
  pending: 0,
  closed: 0,
  urgent: 0,
  high: 0,
  newToday: 0,
};

export default function Support() {
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [stats, setStats] = useState<EmailStats>(defaultStats);
  const [selectedThread, setSelectedThread] = useState<ThreadWithMessages | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({
    fromName: '',
    fromAddress: '',
    subject: '',
    message: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [threadsRes, statsRes] = await Promise.all([
        fetch('/api/emails/threads'),
        fetch('/api/emails/stats'),
      ]);

      if (threadsRes.ok) {
        const data = await threadsRes.json();
        setThreads(data.threads || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch email data:', err);
      setError('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const openThread = async (threadId: string) => {
    try {
      const res = await fetch(`/api/emails/threads/${threadId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedThread(data);
      }
    } catch (err) {
      console.error('Failed to fetch thread:', err);
    }
  };

  const updateThreadStatus = async (threadId: string, status: string) => {
    try {
      const res = await fetch(`/api/emails/threads/${threadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        fetchData();
        if (selectedThread?.id === threadId) {
          setSelectedThread({ ...selectedThread, status: status as any });
        }
      }
    } catch (err) {
      console.error('Failed to update thread:', err);
    }
  };

  const sendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedThread || !replyMessage.trim() || sendingReply) return;

    setSendingReply(true);
    try {
      const res = await fetch(`/api/emails/threads/${selectedThread.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: replyMessage }),
      });
      if (res.ok) {
        const newMessage = await res.json();
        // Add the new message to the thread
        setSelectedThread({
          ...selectedThread,
          messages: [...selectedThread.messages, newMessage],
          status: 'pending',
        });
        setReplyMessage('');
        // Refresh the thread list to update counts
        fetchData();
      }
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setSendingReply(false);
    }
  };

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTicket.fromAddress || !newTicket.subject || !newTicket.message || creatingTicket) return;

    setCreatingTicket(true);
    try {
      const res = await fetch('/api/emails/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromName: newTicket.fromName || newTicket.fromAddress,
          fromAddress: newTicket.fromAddress,
          subject: newTicket.subject,
          bodyText: newTicket.message,
          priority: newTicket.priority,
        }),
      });
      if (res.ok) {
        setShowCreateModal(false);
        setNewTicket({
          fromName: '',
          fromAddress: '',
          subject: '',
          message: '',
          priority: 'normal',
        });
        fetchData();
      } else {
        alert('Failed to create ticket. Please try again.');
      }
    } catch (err) {
      console.error('Failed to create ticket:', err);
      alert('Failed to create ticket. Please try again.');
    } finally {
      setCreatingTicket(false);
    }
  };

  const filteredThreads = threads.filter((thread) => {
    const matchesSearch =
      searchQuery === '' ||
      thread.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      thread.fromAddress.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (thread.fromName && thread.fromName.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || thread.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || thread.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)}h ago`;
    } else if (diffHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertCircle size={14} className="status-icon open" />;
      case 'pending':
        return <Clock size={14} className="status-icon pending" />;
      case 'closed':
        return <CheckCircle size={14} className="status-icon closed" />;
      default:
        return null;
    }
  };

  const getPriorityClass = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'priority-urgent';
      case 'high':
        return 'priority-high';
      case 'normal':
        return 'priority-normal';
      case 'low':
        return 'priority-low';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Support Tickets</h1>
        </div>
        <div className="loading-state">Loading support tickets...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Support Tickets</h1>
        </div>
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="page-container support-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Support Tickets</h1>
          <p>Email support threads from Postmark</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={fetchData}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} />
            Create Ticket
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="support-stats">
        <div className="support-stat">
          <div className="support-stat-icon">
            <Mail size={20} />
          </div>
          <div className="support-stat-content">
            <div className="support-stat-value">{stats.total}</div>
            <div className="support-stat-label">Total Threads</div>
          </div>
        </div>
        <div className="support-stat open">
          <div className="support-stat-icon">
            <AlertCircle size={20} />
          </div>
          <div className="support-stat-content">
            <div className="support-stat-value">{stats.open}</div>
            <div className="support-stat-label">Open</div>
          </div>
        </div>
        <div className="support-stat pending">
          <div className="support-stat-icon">
            <Clock size={20} />
          </div>
          <div className="support-stat-content">
            <div className="support-stat-value">{stats.pending}</div>
            <div className="support-stat-label">Pending</div>
          </div>
        </div>
        <div className="support-stat urgent">
          <div className="support-stat-icon">
            <AlertCircle size={20} />
          </div>
          <div className="support-stat-content">
            <div className="support-stat-value">{stats.urgent + stats.high}</div>
            <div className="support-stat-label">High Priority</div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="support-content">
        {/* Thread List */}
        <div className="thread-list-panel">
          {/* Filters */}
          <div className="support-toolbar">
            <div className="search-box">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search threads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="pending">Pending</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Priority</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          {/* Thread List */}
          <div className="thread-list">
            {filteredThreads.length === 0 ? (
              <div className="empty-state">
                <Mail size={48} />
                <h3>No threads found</h3>
                <p>Try adjusting your search or filters</p>
              </div>
            ) : (
              filteredThreads.map((thread) => (
                <div
                  key={thread.id}
                  className={`thread-item ${selectedThread?.id === thread.id ? 'selected' : ''}`}
                  onClick={() => openThread(thread.id)}
                >
                  <div className="thread-header">
                    <div className="thread-status">
                      {getStatusIcon(thread.status)}
                      <span className={`priority-badge ${getPriorityClass(thread.priority)}`}>
                        {thread.priority}
                      </span>
                    </div>
                    <span className="thread-time">{formatDate(thread.updatedAt)}</span>
                  </div>
                  <div className="thread-subject">{thread.subject}</div>
                  <div className="thread-from">
                    <User size={12} />
                    {thread.fromName || thread.fromAddress}
                  </div>
                  <div className="thread-meta">
                    <span className="message-count">
                      <MessageSquare size={12} />
                      {thread.messageCount || 0} messages
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Thread Detail */}
        <div className="thread-detail-panel">
          {selectedThread ? (
            <>
              <div className="thread-detail-header">
                <h2>{selectedThread.subject}</h2>
                <div className="thread-detail-meta">
                  <span className="from-info">
                    <User size={14} />
                    {selectedThread.fromName || selectedThread.fromAddress}
                    {selectedThread.fromName && (
                      <span className="email-address">&lt;{selectedThread.fromAddress}&gt;</span>
                    )}
                  </span>
                  <span className={`status-badge ${selectedThread.status}`}>
                    {selectedThread.status}
                  </span>
                </div>
                <div className="thread-actions">
                  {selectedThread.status !== 'closed' && (
                    <>
                      {selectedThread.status === 'open' && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => updateThreadStatus(selectedThread.id, 'pending')}
                        >
                          Mark Pending
                        </button>
                      )}
                      <button
                        className="btn btn-primary"
                        onClick={() => updateThreadStatus(selectedThread.id, 'closed')}
                      >
                        <CheckCircle size={14} />
                        Close Thread
                      </button>
                    </>
                  )}
                  {selectedThread.status === 'closed' && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => updateThreadStatus(selectedThread.id, 'open')}
                    >
                      Reopen Thread
                    </button>
                  )}
                </div>
              </div>

              <div className="messages-list">
                {selectedThread.messages.map((message) => (
                  <div key={message.id} className={`message-item ${message.direction}`}>
                    <div className="message-header">
                      <span className="message-direction">
                        {message.direction === 'inbound' ? 'Customer' : 'Support'}
                      </span>
                      <span className="message-time">{formatDate(message.sentAt)}</span>
                    </div>
                    <div className="message-body">
                      {message.bodyText || (
                        <div
                          className="message-html"
                          dangerouslySetInnerHTML={{ __html: message.bodyHtml || '' }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Form */}
              {selectedThread.status !== 'closed' && (
                <form className="reply-form" onSubmit={sendReply}>
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                    disabled={sendingReply}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!replyMessage.trim() || sendingReply}
                  >
                    <Send size={16} />
                    {sendingReply ? 'Sending...' : 'Send Reply'}
                  </button>
                </form>
              )}
            </>
          ) : (
            <div className="no-thread-selected">
              <Mail size={64} />
              <h3>Select a thread</h3>
              <p>Choose a conversation from the list to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal create-ticket-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Support Ticket</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateTicket}>
              <div className="form-row">
                <div className="form-group">
                  <label>Contact Name</label>
                  <input
                    type="text"
                    value={newTicket.fromName}
                    onChange={(e) => setNewTicket({ ...newTicket, fromName: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    value={newTicket.fromAddress}
                    onChange={(e) => setNewTicket({ ...newTicket, fromAddress: e.target.value })}
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group flex-2">
                  <label>Subject *</label>
                  <input
                    type="text"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    placeholder="Brief description of the issue"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value as any })}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Message *</label>
                <textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                  placeholder="Describe the issue in detail..."
                  rows={6}
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creatingTicket}>
                  <Plus size={16} />
                  {creatingTicket ? 'Creating...' : 'Create Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
